/**
 * Knowledge base (PRD-0317). Column-driven list filters, typed
 * Create/Update/Response, `If-Match` through `ifMatchHeaders` on every
 * mutation that carries a version — the `proceduralMemory.ts` shape. The five
 * lifecycle transitions are the one exception: `if_match` travels inside
 * `TransitionRequest`'s body, not an `If-Match` header (D-5), because the
 * route also accepts no assertion at all (the field is optional).
 */

import type {
  ArticleBodyReplace,
  ArticleCreate,
  ArticleListFilters,
  ArticleListResponse,
  ArticleResponse,
  ArticleRevision,
  ArticleUpdate,
  ArticleValidationReport,
  ArticleValidationRequest,
  AttachmentDuplicateDetail,
  AttachmentLimitsResponse,
  AttachmentResponse,
  CollectionCreate,
  CollectionKind,
  CollectionResponse,
  CollectionUpdate,
  DeprecateRequest,
  PublisherResolution,
  PublishersReplace,
  TransitionRequest,
} from '@/types/knowledgeBase'
import type {
  CollectionOrientationResponse,
  CollectionOrientationWrite,
  CollectionOverviewResponse,
  CollectionStructureRequest,
  OrientationHistoryRequest,
  OrientationHistoryResponse,
  StructurePage,
  StructureView,
} from '@/types/collectionOverview'
import { apiClient, serverBudgetTimeout } from './client'
import { ifMatchHeaders } from './ifMatch'
import { repeatedKeyParamsSerializer } from './queryParams'
import { isApiResponseError, isConflictReason } from '@/utils/errors'

const BASE = '/api/v1/knowledge-base'

// ---------------------------------------------------------------------------
// Attachment upload — the raw-body POST, its served timeout and its typed
// 409 outcome.
// ---------------------------------------------------------------------------

export interface AttachmentUploadOptions {
  fileName: string
  limits: AttachmentLimitsResponse
}

/** The 409 reason for a *different* image sent under a name already in use. */
const NAME_TAKEN_REASON = 'attachment_name_taken'

/**
 * What `attachments.upload` resolves with.
 *
 * A `created` row is a normal (201) response. The other two are the 409s a
 * caller can act on rather than report, and they are opposites: `duplicate`
 * is these same bytes already on the article (`attachment_duplicate`, D-6) —
 * `detail.state === 'live'` means the earlier upload landed and
 * `detail.attachment_id` is the row to use without sending the file again,
 * `'reserved'` means wait `detail.retry_after_seconds` and re-read — while
 * `name_taken` is *different* bytes under a name the article already uses,
 * whose only repair is a different name. Every remaining failure (the two
 * limit reasons, an archived article, network/timeout) still rejects, because
 * nothing the caller does with the same request would change the answer.
 */
export type AttachmentUploadResult =
  | { outcome: 'created'; attachment: AttachmentResponse }
  | { outcome: 'duplicate'; detail: AttachmentDuplicateDetail }
  | { outcome: 'name_taken'; fileName: string }

/**
 * Read the 409 `attachment_duplicate` detail back out of a rejected upload.
 *
 * The shared client's response interceptor normalises every error body to a
 * flattened `detail: string`; a plain-object detail with no recognised shape
 * (`reason`, `identifier`, `errors`) falls through to `JSON.stringify` (the
 * "nothing is silently dropped" branch of `normalizeApiErrorDetail`) — which
 * is exactly the shape `AttachmentDuplicateDetail` takes. Any other 409 on
 * this route (`attachment_limit`, an archived article, …) carries a plain
 * message string that is not valid JSON, so it falls through untouched.
 */
function readAttachmentDuplicateDetail(
  error: unknown
): AttachmentDuplicateDetail | null {
  if (!isApiResponseError(error) || error.status !== 409 || !error.detail)
    return null
  try {
    const parsed: unknown = JSON.parse(error.detail)
    if (
      parsed !== null &&
      typeof parsed === 'object' &&
      'attachment_id' in parsed &&
      'state' in parsed
    ) {
      return parsed as AttachmentDuplicateDetail
    }
    return null
  } catch {
    return null
  }
}

/** Whether a rejected upload is the name clash. */
function isAttachmentNameTaken(error: unknown): boolean {
  return isConflictReason(error, NAME_TAKEN_REASON)
}

export const knowledgeBase = {
  collections: {
    list: async (kind?: CollectionKind): Promise<CollectionResponse[]> => {
      const response = await apiClient.get<CollectionResponse[]>(
        `${BASE}/collections/`,
        {
          params: { kind },
        }
      )
      return response.data
    },

    get: async (id: string): Promise<CollectionResponse> => {
      const response = await apiClient.get<CollectionResponse>(
        `${BASE}/collections/${id}/`
      )
      return response.data
    },

    create: async (data: CollectionCreate): Promise<CollectionResponse> => {
      const response = await apiClient.post<CollectionResponse>(
        `${BASE}/collections/`,
        data
      )
      return response.data
    },

    update: async (
      id: string,
      data: CollectionUpdate,
      ifMatch: number
    ): Promise<CollectionResponse> => {
      const response = await apiClient.patch<CollectionResponse>(
        `${BASE}/collections/${id}/`,
        data,
        ifMatchHeaders(ifMatch)
      )
      return response.data
    },

    publishers: async (id: string): Promise<PublisherResolution> => {
      const response = await apiClient.get<PublisherResolution>(
        `${BASE}/collections/${id}/publishers/`
      )
      return response.data
    },

    // No If-Match: PublishersReplace carries no version — the set is an
    // editorial roster replaced wholesale, not a versioned field (D-2).
    setPublishers: async (
      id: string,
      data: PublishersReplace
    ): Promise<PublisherResolution> => {
      const response = await apiClient.put<PublisherResolution>(
        `${BASE}/collections/${id}/publishers/`,
        data
      )
      return response.data
    },

    overview: async (
      id: string,
      signal?: AbortSignal
    ): Promise<CollectionOverviewResponse> => {
      const response = await apiClient.get<CollectionOverviewResponse>(
        `${BASE}/collections/${id}/overview/`,
        { signal }
      )
      return response.data
    },

    // A cursor is valid only with the filters it was issued for, and only
    // while the overview revision it was issued under holds: a later revision
    // answers 409 `overview_revision_changed`.
    structure: async <V extends StructureView>(
      id: string,
      request: CollectionStructureRequest<V>,
      signal?: AbortSignal
    ): Promise<StructurePage<V>> => {
      const response = await apiClient.get<StructurePage<V>>(
        `${BASE}/collections/${id}/structure/`,
        { params: request, signal }
      )
      return response.data
    },

    orientation: async (
      id: string,
      signal?: AbortSignal
    ): Promise<CollectionOrientationResponse> => {
      const response = await apiClient.get<CollectionOrientationResponse>(
        `${BASE}/collections/${id}/orientation/`,
        { signal }
      )
      return response.data
    },

    // If-Match is the collection's metadata_version: a save replaces the whole
    // orientation, so it must never overwrite a revision its author did not read.
    saveOrientation: async (
      id: string,
      data: CollectionOrientationWrite,
      ifMatch: number
    ): Promise<CollectionOrientationResponse> => {
      const response = await apiClient.put<CollectionOrientationResponse>(
        `${BASE}/collections/${id}/orientation/`,
        data,
        ifMatchHeaders(ifMatch)
      )
      return response.data
    },

    orientationRevisions: async (
      id: string,
      page: OrientationHistoryRequest,
      signal?: AbortSignal
    ): Promise<OrientationHistoryResponse> => {
      const response = await apiClient.get<OrientationHistoryResponse>(
        `${BASE}/collections/${id}/orientation/revisions/`,
        { params: page, signal }
      )
      return response.data
    },
  },

  articles: {
    list: async (filters: ArticleListFilters): Promise<ArticleListResponse> => {
      const response = await apiClient.get<ArticleListResponse>(
        `${BASE}/articles/`,
        {
          params: filters,
          paramsSerializer: repeatedKeyParamsSerializer,
        }
      )
      return response.data
    },

    get: async (
      id: string,
      revision?: ArticleRevision
    ): Promise<ArticleResponse> => {
      const response = await apiClient.get<ArticleResponse>(
        `${BASE}/articles/${id}/`,
        {
          params: { revision },
        }
      )
      return response.data
    },

    getByNumber: async (
      articleNumber: number,
      revision?: ArticleRevision
    ): Promise<ArticleResponse> => {
      const response = await apiClient.get<ArticleResponse>(
        `${BASE}/articles/by-number/${articleNumber}/`,
        { params: { revision } }
      )
      return response.data
    },

    // text/markdown, not JSON — responseType: 'text' is what stops the shared
    // client's default transform from attempting a JSON.parse of the body.
    document: async (
      id: string,
      revision?: ArticleRevision
    ): Promise<string> => {
      const response = await apiClient.get<string>(
        `${BASE}/articles/${id}/document/`,
        {
          params: { revision },
          responseType: 'text',
        }
      )
      return response.data
    },

    create: async (data: ArticleCreate): Promise<ArticleResponse> => {
      const response = await apiClient.post<ArticleResponse>(
        `${BASE}/articles/`,
        data
      )
      return response.data
    },

    update: async (
      id: string,
      data: ArticleUpdate,
      ifMatch: number
    ): Promise<ArticleResponse> => {
      const response = await apiClient.patch<ArticleResponse>(
        `${BASE}/articles/${id}/`,
        data,
        ifMatchHeaders(ifMatch)
      )
      return response.data
    },

    replaceBody: async (
      id: string,
      data: ArticleBodyReplace,
      ifMatch: number
    ): Promise<ArticleResponse> => {
      const response = await apiClient.put<ArticleResponse>(
        `${BASE}/articles/${id}/body/`,
        data,
        ifMatchHeaders(ifMatch)
      )
      return response.data
    },

    // The five lifecycle verbs. `if_match` (when the caller supplies one)
    // travels inside the request body, not a header — see the module docstring.
    submit: async (
      id: string,
      data: TransitionRequest
    ): Promise<ArticleResponse> => {
      const response = await apiClient.post<ArticleResponse>(
        `${BASE}/articles/${id}/submit/`,
        data
      )
      return response.data
    },

    withdraw: async (
      id: string,
      data: TransitionRequest
    ): Promise<ArticleResponse> => {
      const response = await apiClient.post<ArticleResponse>(
        `${BASE}/articles/${id}/withdraw/`,
        data
      )
      return response.data
    },

    publish: async (
      id: string,
      data: TransitionRequest
    ): Promise<ArticleResponse> => {
      const response = await apiClient.post<ArticleResponse>(
        `${BASE}/articles/${id}/publish/`,
        data
      )
      return response.data
    },

    deprecate: async (
      id: string,
      data: DeprecateRequest
    ): Promise<ArticleResponse> => {
      const response = await apiClient.post<ArticleResponse>(
        `${BASE}/articles/${id}/deprecate/`,
        data
      )
      return response.data
    },

    archive: async (
      id: string,
      data: TransitionRequest
    ): Promise<ArticleResponse> => {
      const response = await apiClient.post<ArticleResponse>(
        `${BASE}/articles/${id}/archive/`,
        data
      )
      return response.data
    },

    validate: async (
      data: ArticleValidationRequest
    ): Promise<ArticleValidationReport> => {
      const response = await apiClient.post<ArticleValidationReport>(
        `${BASE}/articles/validate/`,
        data
      )
      return response.data
    },
  },

  attachments: {
    limits: async (): Promise<AttachmentLimitsResponse> => {
      const response = await apiClient.get<AttachmentLimitsResponse>(
        `${BASE}/attachments/limits/`
      )
      return response.data
    },

    // Raw File body, Content-Type = the file's own MIME type, and a
    // per-request timeout computed from the served budget (never the shared
    // client's DEFAULT_TIMEOUT_MS) — a caller must be allowed to wait out the
    // whole verb it was told about.
    upload: async (
      articleId: string,
      file: File,
      { fileName, limits }: AttachmentUploadOptions
    ): Promise<AttachmentUploadResult> => {
      try {
        const response = await apiClient.post<AttachmentResponse>(
          `${BASE}/articles/${articleId}/attachments/`,
          file,
          {
            params: { file_name: fileName },
            headers: { 'Content-Type': file.type },
            ...serverBudgetTimeout(limits.upload_budget_seconds),
          }
        )
        return { outcome: 'created', attachment: response.data }
      } catch (error) {
        const detail = readAttachmentDuplicateDetail(error)
        if (detail) return { outcome: 'duplicate', detail }
        if (isAttachmentNameTaken(error))
          return { outcome: 'name_taken', fileName }
        throw error
      }
    },

    fetch: async (articleId: string, attachmentId: string): Promise<Blob> => {
      const response = await apiClient.get<Blob>(
        `${BASE}/articles/${articleId}/attachments/${attachmentId}/`,
        { responseType: 'blob' }
      )
      return response.data
    },

    remove: async (articleId: string, attachmentId: string): Promise<void> => {
      await apiClient.delete(
        `${BASE}/articles/${articleId}/attachments/${attachmentId}/`
      )
    },
  },
}
